import { controlTag } from "@/form/form-control-initializer";
import { Expandable } from "@/expandable";
import { MultiTreeItemSubselect } from "./multi-tree-item-subselect";
import { TreeItem } from "./tree-item";
import { SingleTreeItemSubselect } from "./single-tree-item-subselect";
import { SingleSubselect, Subselect, TreeSelection } from "./tree-select";
import { TreeControl } from "./tree-control";
import { TreeSelectControllerSettings } from "./tree-select-controller";

type HydratedSubselect = SingleSubselect | MultiTreeItemSubselect;

export class TreeSelectHydrator {

    constructor(private settings: TreeSelectControllerSettings) {}

    hydrateBranch(element: HTMLElement): Promise<HydratedSubselect[]> {
        return this.hydrateControls(element)
            .then(controls => {
                const itemSelects = this.hydrateItemSelects(element);
                return [...controls, ...itemSelects];
            });
    }

    hydrateSubselect(selection: TreeSelection<Subselect<unknown>>, html: string): HydratedSubselect | null {
        const container = selection.parent.getSubselectContainer(selection);
        if (!container) {
            return null;
        }

        container.innerHTML = html;
        return this.hydrateSubselectElement(container.firstElementChild as HTMLElement);
    }

    private hydrateControls(element: HTMLElement): Promise<TreeControl[]> {
        const controlEls = element.querySelectorAll<HTMLElement>(`.${this.settings.controlClass}`);
        return controlTag.getAll(controlEls)
            .then(controls => controls.map(control => new TreeControl(control)));
    }

    private hydrateItemSelects(element: HTMLElement): HydratedSubselect[] {
        const itemSelectElements = element.querySelectorAll<HTMLElement>(`.${this.settings.itemSelectClass}`);
        return Array.from(itemSelectElements).map(itemSelectEl => this.hydrateItemSelect(itemSelectEl));
    }

    private hydrateItemSelect(element: HTMLElement): HydratedSubselect {
        // Hydrate only the immediate items in this subselect.
        // Nested descendants belong to child subselects and must not be attached here.
        const items = Array.from(element.children)
            .filter((child) => child.classList.contains(this.settings.itemClass))
            .map(itemEl => this.hydrateItem(itemEl as HTMLElement));
            
        if (this.settings.multiple) {
            return new MultiTreeItemSubselect(element, items);
        }

        return new SingleTreeItemSubselect(element, items);
    }

    private hydrateItem(element: HTMLElement): TreeItem {
        const expandableList = this.hydrateExpandableList(element);
        return new TreeItem(element, this.settings.itemSettings, expandableList);
    }

    private hydrateExpandableList(treeItem: HTMLElement): Expandable | null {
        const expandable = treeItem.classList.contains(this.settings.listClass);
        if (!expandable) {
            return null;
        }

        return new Expandable(treeItem, this.settings.listSettings);
    }

    private hydrateSubselectElement(subselectEl?: HTMLElement): HydratedSubselect | null {
        if (subselectEl?.classList.contains(this.settings.itemSelectClass)) {
            return this.hydrateItemSelect(subselectEl);
        }

        return null;
    }
}
